<template>
  <div id="app">
    <div v-if="QReserveSandboxMode" id="sandbox-banner">
      <strong>
        <font-awesome-icon :icon="faIconsLocal.faFlask" />
        QReserve Sandbox Server: "The Lab"
      </strong>
      <template v-if="expandSandboxBar">
        <p>
          This server is a "sandbox" server that uses a copy of your real data.
        </p>
        <p>
          All data is
          <strong>reset</strong>
          at least once per week and no changes you make here will be available
          in the production platform.
        </p>
        <p>
          Please review the
          <!-- eslint-disable-next-line max-len -->
          <a
            href="https://get.qreserve.com/documentation/qreserve-sandbox-server-the-lab"
            target="_blank">
            limitations of this platform
          </a>
          .
        </p>
        <p>
          <br />
          <button class="btn btn-link inline" @click="expandSandboxBar = false">
            <font-awesome-icon :icon="faIconsLocal.faCaretUpSolid" />
            Hide this warning
            <font-awesome-icon :icon="faIconsLocal.faCaretUpSolid" />
          </button>
        </p>
      </template>
      <template v-if="!expandSandboxBar">
        <p>
          <button class="btn btn-link inline" @click="expandSandboxBar = true">
            <font-awesome-icon :icon="faIconsLocal.faCaretDownSolid" />
            Show full warning
            <font-awesome-icon :icon="faIconsLocal.faCaretDownSolid" />
          </button>
        </p>
      </template>
    </div>
    <div v-if="QReserveReadOnlyMode" id="read-only-banner">
      <strong>Read Only Server Warning</strong>
      <br />
      This server is an emergency read only backup server. No actions performed
      will be saved.
    </div>
    <div v-if="showOldBrowserWarning" id="old-browser-banner">
      <strong>Please Update Your Browser</strong>
      <br />
      It appears that your browser is an older version that may not support all
      of the functionality required for QReserve. Please consider updating to a
      more recent version.
    </div>
    <div v-if="newVersion" class="new-version hide-print">
      New Version Available
      <div><small>Reload for the update!</small></div>
      <button
        class="btn btn-sm btn-primary"
        style="
          width: 100%;
          margin: 0 -1rem -1rem -1rem;
          color: #fff;
          background-color: #ed2a7a;
        "
        @click="reloadNow()">
        <font-awesome-icon :icon="faIconsLocal.faSyncSolid" />
        Reload now
      </button>
    </div>
    <toast :html="true" :position="mobileView ? 's' : 'ne'"></toast>
    <template v-if="$route.matched.length">
      <div
        v-if="!$route.meta.fullscreen"
        style="height: 100%"
        id="app-base-root">
        <div
          class="w3-sidebar w3-bar-block w3-collapse w3-animate-left sidebar-z hide-print"
          role="navigation"
          :class="{ 'expanded-menu': !shrunkMenu, 'shrunk-menu': shrunkMenu }"
          style="overflow-x: hidden"
          id="main-sidebar">
          <main-menu
            @shrink="shrinkMenu"
            @expand="expandMenu"
            :shrunk="shrunkMenu"
            :closeSidebar="closeSidebar"></main-menu>
        </div>
        <div
          class="w3-main mainbody"
          :class="{ 'expanded-body': shrunkMenu, 'shrunk-body': !shrunkMenu }"
          role="main">
          <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
          <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
          <div v-if="overlay" class="overlay" @click="closeSidebar()"></div>
          <!-- esline-enable-->
          <!-- esline-enable-->

          <!-- Top Messages -->
          <!-- Disabled but left in code as an example.
          <div
            v-if="$store.state.auth?.authUser &&
                  $store.state.auth?.authUser?.user_id">
            <top-message messageKey="20201019-ICSFeedUpdate"
              title="Important Message"
              :titleIcon="faIconsLocal.faExclamationTriangleSolid">
              If you use <strong>calendar ICS export feeds</strong>, you
              must update to new feed URLs to
              continue using this feature. Access new URLs via the
              Export Calendar links on each calendar.
            </top-message>
          </div>
          -->
          <div v-if="authUserUnsubscribed">
            <top-message
              messageKey="email-unsubscribed"
              title="You are not receiving emails from QReserve"
              :allowHiding="false"
              :titleIcon="faIconsLocal.faExclamationTriangleSolid">
              Your primary email address is currently
              <strong>
                unsubscribed from receiving any emails from the QReserve
                platform.
              </strong>
              <p style="margin-bottom: 0">
                You can re-activate emails and refine your settings on
                <router-link
                  style="color: #ffffff"
                  class="inline"
                  :to="{
                    name: 'user',
                    params: { userId: 'self' },
                    query: { tab: 'notifications' },
                  }">
                  your profile page
                </router-link>
                .
              </p>
            </top-message>
          </div>

          <div
            v-if="debugMode && developerStuff"
            class="qdev-bar hide-print"
            role="button"
            tabindex="0"
            @dblclick="hideQDev = true"
            @click="expandQDev = !expandQDev"
            @keydown.enter="expandQDev = !expandQDev"
            :class="{
              hidden: hideQDev,
              expanded: expandQDev,
            }">
            <div class="toolbar">
              <strong>
                <font-awesome-icon :icon="faIconsLocal.faCodeBranchSolid" />
                QDev
              </strong>
              <span class="text-tt">{{ commitId }}</span>
              |
              <strong>GETs:</strong>
              {{ developerStuff.general_stats.num_requests }}
              |
              <strong>Queries:</strong>
              {{ developerStuff.general_stats.num_queries }}
              |
              <strong>Problems APIs:</strong>
              {{ developerStuff.problem_requests.length }}
            </div>
            <div class="details" v-if="expandQDev">
              <div
                class="request"
                v-for="(api, idx) in developerStuff.problem_requests"
                :key="`problem-request-idx-${idx}`">
                <table>
                  <tbody>
                    <tr>
                      <th colspan="2">
                        {{ api.url }}
                      </th>
                    </tr>
                    <tr>
                      <th style="width: 6rem">Reasons</th>
                      <td>
                        <ul>
                          <li v-for="reason in api.reasons" :key="reason">
                            {{ reason }}
                          </li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <th style="width: 6rem">Queries</th>
                      <td>
                        {{ api.num_queries }}
                      </td>
                    </tr>
                    <tr v-if="api.reasons.indexOf('High repeats') !== -1">
                      <th style="width: 6rem">Repeats</th>
                      <td>
                        <table>
                          <tbody>
                            <tr
                              v-for="repeats in api.response.data.developer
                                .database.high_repeats"
                              :key="`${String(repeats[0])}-${String(repeats[1])}`">
                              <th v-if="repeats[0] > 10">{{ repeats[0] }}</th>
                              <td v-if="repeats[0] > 10">{{ repeats[1] }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    <tr v-if="api.reasons.indexOf('Slow queries') !== -1">
                      <th style="width: 6rem">Slow</th>
                      <td>
                        <table>
                          <tbody>
                            <tr
                              v-for="call in api.response.data.developer
                                .database.slow_calls"
                              :key="JSON.stringify(call)">
                              <th v-if="call.dur > 0.3">
                                {{ call.dur.toFixed(2) }}s
                              </th>
                              <td v-if="call.dur > 0.3">
                                {{ call.context }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="header hide-print">
            <button
              class="btn btn-link btn-lg w3-hide-large hamburger"
              @click="openSidebar()">
              <font-awesome-icon :icon="faIconsLocal.faBars" />
            </button>
            <router-link
              :to="{ name: 'dashboard' }"
              tabindex="-1"
              role="presentation">
              <img
                v-show="logoSidebarUrl"
                :src="logoSidebarUrl"
                alt="QReserve Logo"
                class="logo w3-hide-large" />
              <img
                v-show="!logoSidebarUrl"
                alt="QReserve Logo"
                src="~assets/img/qreserve-white.svg"
                class="logo w3-hide-large" />
            </router-link>
          </div>
          <div
            class="w3-container"
            :style="mainContainerStyle"
            id="main-container">
            <router-view :key="$route.path + wsNonce"></router-view>
          </div>
        </div>
      </div>
      <div v-if="$route.meta.fullscreen" class="fullscreen">
        <router-view :key="$route.path + wsNonce"></router-view>
      </div>
    </template>
    <template v-else-if="$route.fullPath == '/'">
      <span class="loading-route" role="main">Please Wait</span>
    </template>
    <template v-else>
      <span class="loading-route" role="main">
        <img src="~assets/img/qreserve.svg" alt="QReserve Logo" />
        <div class="msg">Page Not Found</div>
        <div v-if="authUser && authUser?.user_id">
          <router-link class="link" :to="{ name: 'dashboard' }">
            Return to dashboard
          </router-link>
        </div>
        <div v-if="!authUser || authUser?.user_id">
          <router-link class="link" :to="{ name: 'login' }">
            Return to sign in page
          </router-link>
        </div>
      </span>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';
import { detect } from 'detect-browser';

import { CI_TESTING } from 'assets/js/api/environment';
// @ts-ignore
import {
  FontAwesomeIcon,
  FontAwesomeLayers,
} from '@fortawesome/vue-fontawesome';

import * as api from 'assets/js/api';

// import lang from 'element-ui/lib/locale/lang/en';
// import locale from 'element-ui/lib/locale';

import { faIcons } from 'assets/js/mixins/icons';
import { Toast } from 'components/Widgets/Toast';

import withMobileView from 'assets/js/composables/withMobileView';

// TS requires .vue extensions, so don't let eslint complain here.
// eslint-disable-next-line import/extensions
import MainMenu from 'components/Sidebar/MainMenu.vue';
// eslint-disable-next-line import/extensions
import TopMessage from 'components/Widgets/TopMessage/TopMessage.vue';

import Autologout from './Autologout';

// Set element's language to EN.
// locale.use(lang);

const NEW_VERSION_CHECK_CYCLE_MS = 600000;
const SIZE_HEADER_VIEW = 992;
const SIZE_MOBILE_VIEW = 550;

export default {
  components: {
    FontAwesomeIcon,
    FontAwesomeLayers,
    MainMenu,
    Toast,
    TopMessage,
  },

  mixins: [Autologout],

  setup() {
    const { mobileView, windowWidth } = withMobileView({
      pixelWidthCutoff: SIZE_MOBILE_VIEW,
    });
    return {
      mobileView,
      windowWidth,
    };
  },

  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  computed: {
    authUser() {
      return this.$store.state.auth?.authUser;
    },

    authUserUnsubscribed() {
      if (!this.$store.state.auth?.authUser?.emails) return false;
      const primaryEmail = _.find(
        this.$store.state.auth.authUser.emails,
        (e) => e.primary,
      );
      return primaryEmail?.unsubscribed;
    },

    commitId() {
      // Returns the short hash of the commit.
      return process.env.VERSION.replace(/"/g, '').substring(0, 7);
    },

    // debugMode returns true if we're running the development server.
    debugMode() {
      return this.ENV === 'development';
    },

    developerStuff() {
      return this.$store.state.developer;
    },

    // error is the global error state.
    error() {
      return this.$store.state.error;
    },

    // headerView returns a boolean indicating whether or not the screen is
    // current in compressed view based on the window width.
    headerView() {
      return this.windowWidth <= SIZE_HEADER_VIEW;
    },

    logoSidebarUrl() {
      return this.$store.getters.logoSidebarUrl;
    },

    // computes styles to apply to the main container typically based on
    // the route.
    mainContainerStyle() {
      const fullScreen = [
        'siteauditlog',
        'siteresourcesrates',
        'siteresources',
        'siteusers',
        'siteuserstraining',
        'sitetraining',
        'siteusage',
      ];
      if (fullScreen.indexOf(_.get(this.$route, 'name', '')) !== -1) {
        return { 'max-width': 'none' };
      }
      return {};
    },

    themeCss() {
      return this.$store.getters.themeCss ?? '';
    },

    themeStyle() {
      return this.$store.getters.themeStyle;
    },

    // wsNonce is used in the router-view key to indicate whether or not the
    // router-view component needs to be refreshed because the internal
    // websocket had to be restarted.
    wsNonce() {
      // Upon first load this is always 0 then immediately turns to 1.
      // This causes router-view to mount twice. Therefore, we return 1
      // when it is zero to "Skip" this first update.
      const wsNonce = this.mainChContainer.nonce;
      if (wsNonce === 0) return 1;
      return wsNonce;
    },
  },

  created() {
    api.Init(this);

    // @ts-ignore
    this.mainChContainer = window.fetchMainChContainer();

    // Spawn a routine that checks for the latest version of the app.
    // If a new version has been released, the page reloads.
    if (this.ENV === 'production') {
      this.doVersionCheck();
      this.checkInterval = setInterval(
        this.doVersionCheck,
        NEW_VERSION_CHECK_CYCLE_MS,
      );
    }
  },

  data() {
    function applyThemeToDocument(themeStyle) {
      Object.entries(themeStyle).forEach(([k, v]) => {
        if (v) {
          document.documentElement.style.setProperty(k, v.toString());
        }
      });
    }

    return {
      applyThemeToDocument,
      checkInterval: null,
      ENV: CI_TESTING ? 'testing' : process.env.NODE_ENV,
      faIconsLocal: faIcons,
      hideQDev: false,
      expandQDev: false,
      shrunkMenu: false,
      expandSandboxBar: true,
      mainChContainer: { nonce: 0 },
      newVersion: false,
      overlay: false,
      showOldBrowserWarning: false,
    };
  },

  methods: {
    // clearError clears the error state from the store (not currently used).
    clearError() {
      this.$store.dispatch('ClearError');
    },

    // closeSidebar closes the sidebar element.
    closeSidebar() {
      document.getElementById('main-sidebar').style.display = 'none';
      this.overlay = false;
    },

    // doVersionCheck checks if we are running the latest version.
    async doVersionCheck() {
      // Handle new versions.
      const version = JSON.parse(process.env.VERSION);
      const url = api.urls.VERSION_CHECK(version);
      const resp = await api.Get(url, null, null, 2, false);
      if (resp === 0) {
        // Show the new version pop-up in a few minutes to let the release
        // dust settle. Set to 10 minutes now.
        setTimeout(
          () => {
            this.newVersion = true;
          },
          10 * 60 * 1000,
        );
        clearInterval(this.checkInterval);
      }
    },

    // openSidebar opens the sidebar element.
    openSidebar() {
      document.getElementById('main-sidebar').style.display = 'block';
      this.overlay = true;
      // Move cursor to the menu.
      document
        .getElementById('main-sidebar')
        .getElementsByTagName('a')[2]
        .focus();
    },

    expandMenu() {
      this.shrunkMenu = false;
    },

    shrinkMenu() {
      this.shrunkMenu = true;
    },

    // reloadNow reloads the window.
    reloadNow() {
      window.location.reload();
    },
  },

  async mounted() {
    // Vue does a weird thing where the initial route path is always '/'
    // when booting up, then moves to a different path. If people actually
    // are entering the root ('/') as indicated by the browser path name,
    // we should redirect them to the login page.

    /*
    COMMENTED OUT BY BRANDON
    August 28th, 2022
    With this commented out, entering / or no slash as the path
    still seems to redirec to /login (I think in beforeEach)
    but it also lets us extract the jwt from SSO.

    if (this.$route.fullPath === '/' &&
        (
          _.get(window, 'location.pathname') === '/' ||
          _.get(window, 'location.pathname') === ''
        )) {
      this.$router.Navigate.Login();
    }
    */

    // Tweak the theme to the theme style. Give the browser a little time
    // to apply all the CSS magic.
    this.applyThemeToDocument(this.themeStyle);
    await this.sleep(100);

    // Remove the loader splash after the path has been amended to a
    // legitimate path.
    let timedOut = false;
    setTimeout(() => {
      timedOut = true;
    }, 30000);
    for (;;) {
      if (this.$route.fullPath === '/' && !timedOut) {
        await this.sleep(50);
      } else {
        break;
      }
    }
    const splash = document.getElementById('qreserve-splash-loader');
    if (splash) {
      splash.classList.add('splash-form-loader-complete');
    }
    const vueWrapper = document.getElementById('vue-app-wrapper');
    if (vueWrapper) {
      vueWrapper.setAttribute('style', 'display: initial;');
    }

    // Check for old browsers.
    const browser = detect();
    if (
      browser?.name === 'safari' &&
      (browser?.version?.indexOf('15.3.') === 0 ||
        browser?.version?.indexOf('15.2.') === 0 ||
        browser?.version?.indexOf('15.1.') === 0 ||
        browser?.version?.indexOf('15.0.') === 0 ||
        browser?.version?.indexOf('14.') === 0 ||
        browser?.version?.indexOf('13.') === 0)
    ) {
      this.showOldBrowserWarning = true;
    }
  },

  watch: {
    '$store.state.auth.auth': {
      handler: function handle(val) {
        if (val) {
          this.spawnAutoInactivityLogoutThread();
          this.spawnAutoLogoutThread();
          this.spawnAutoRenewForActivityThread();
        }
        // Clear the thread if the user manually logs out.
        if (!val && _.get(window, 'autoLogoutThread')) {
          window.clearTimeout(_.get(window, 'autoLogoutThread'));
          _.set(window, 'autoLogoutThread', null);
        }
        if (!val && _.get(window, 'autoInactivityLogoutThread')) {
          window.clearTimeout(_.get(window, 'autoInactivityLogoutThread'));
          _.set(window, 'autoInactivityLogoutThread', null);
        }
        if (!val && _.get(window, 'autoLogoutRenewForActivityThread')) {
          window.clearInterval(
            _.get(window, 'autoLogoutRenewForActivityThread'),
          );
          _.set(window, 'autoLogoutRenewForActivityThread', null);
        }
      },
    },
    themeCss: {
      handler: function handle(newCss) {
        this.$nextTick(() => {
          const el = document.getElementById('themeCss');
          if (el) el.innerHTML = newCss;
        });
      },
      deep: true,
    },
    themeStyle: {
      handler: function handle(newThemeStyle) {
        this.applyThemeToDocument(newThemeStyle);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
@import '~assets/css/style';
@import '~assets/css/buttons';
@import '~assets/css/infobox';
@import '~assets/css/combobox';
@import '~assets/css/colors';
@import '~assets/css/sweetalert2';
@import '~assets/css/modal';
@import '~static/css/vuex-toast/vuex-toast.css';

$topbarheight: 55px;
$navopenwidth: 190px;
$navclosedwidth: 40px;

html {
  --color-fg: var(--accent-color, $accentcolor);
}

.sidebar-z {
  z-index: 99;
}

.image-height img {
  height: 5vh;
}

@media only screen and (max-width: 1100px) {
  .image-height img {
    height: 10vh;
  }
}

.right {
  align-content: right;
  align-items: right;
  text-align: right;
}

.w3-container {
  padding: 2rem 3rem;
  max-width: 2400px;
  margin: auto;
}

@media only screen and (max-width: 992px) {
  .w3-container {
    padding: 2rem 1rem;
  }
}

.nav {
  margin: 0;
  padding: 0;
  background-color: var(--primary-color, $primarycolor);
  min-height: 100%;
}

.main {
  margin-left: $navclosedwidth;
  margin-top: $topbarheight;
  background: white;
  flex: 1;
  -ms-flex: 1;
  -webkit-box-flex: 1;
  -moz-box-flex: 1;
  -ms-box-flex: 1;
  box-flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  transition-delay: margin-left 0.2s;
  transition-duration: margin-left 0.5s;
}

.main-open {
  @extend .main;
  margin-left: $navopenwidth;
}

.overlay {
  background: none;
  height: 100vh;
  width: 100vw;
  position: absolute;
}

.header {
  position: relative;
  background-color: $qheadercolor;

  .logo {
    height: 2rem;
    position: absolute;
    top: 1rem;
    left: 4rem;
  }
  a {
    color: #ffffff;
  }
  a:focus {
    box-shadow: none; /* Override a spectre box shadow */
  }

  .header-debug {
    background-color: $qdebugheadercolor;
  }

  .hamburger {
    font-size: 20px;
    color: #ffffff !important;
  }
}

@media only screen and (min-width: 993px) {
  .header {
    display: none;
    .logo,
    .hamburger {
      display: none;
    }
  }
}

.qdev-bar {
  background-color: var(--primary-color, $primarycolor);
  color: var(--active-link-color, $activenavlink);
  border: 1px solid var(--active-link-color, $activenavlink);
  border-top: none;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  text-align: center;
  padding: 0.1rem 1rem;
  cursor: pointer;
  right: 5rem;
  position: fixed;
  z-index: 9999999;
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
  height: auto;
  max-height: 2.4rem;
  font-size: 1.1rem;
  overflow-y: auto;
  transition: 0.5s ease-in-out;
  tt {
    font-size: smaller;
    margin-left: 1rem;
  }
  svg {
    color: red;
  }

  .details {
    color: #def;
    display: none;
    text-align: left;
    th,
    td {
      vertical-align: top;
    }
    ul {
      list-style-type: none;
      margin: 0;
    }
    li {
      margin: 0;
    }
    .request:not(:first-child) {
      border-top: 1px solid var(--active-link-color, $activenavlink);
    }
  }

  &.expanded {
    transition: 0.5s ease-in-out;
    max-height: 90dvh;

    .details {
      display: block;
    }
  }
}
@media only screen and (max-width: 992px) and (min-width: 601px) {
  .qdev-bar {
    position: relative;
    border-radius: 0;
    width: 100%;
    right: 0;
    padding: 0;
    border-left: none;
    border-right: none;
    background-color: var(--primary-color, $primarycolor);
    tt {
      margin-left: 0;
      display: block;
    }
  }
}
@media only screen and (max-width: 600px) {
  .qdev-bar {
    display: none !important;
  }
}

.qdev-bar.hidden {
  display: none;
}

.new-version {
  position: absolute;
  bottom: 0;
  right: 0;
  border-top-left-radius: 4px;
  padding: 1rem 1rem 2rem 1rem;
  background-color: #1e394c;
  color: #fff;
  font-weight: bold;
  z-index: 9999999;
  text-align: center;
  -webkit-box-shadow: -5px 6px 23px -2px rgba(0, 0, 0, 0.41);
  -moz-box-shadow: -5px 6px 23px -2px rgba(0, 0, 0, 0.41);
  box-shadow: -5px 6px 23px -2px rgba(0, 0, 0, 0.41);
}

.profile-link {
  transition: 0.25s;
  padding: 0.4rem;
  border-radius: 0.2em;
}

.profile-link:hover {
  color: $activenavlink;
}

.loading-route {
  font-size: 5rem;
  display: block;
  text-align: center;
  padding-top: 8rem;
  img {
    width: 80%;
    max-width: 500px;
  }
  .msg {
    margin-top: 5rem;
  }
  .link {
    display: block;
    margin-top: 6rem;
    font-size: 2rem;
  }
}

#app {
  .toast-type-info {
    background-color: var(--toast-color, $primarycolor);
  }

  .toast-type-success {
    background-color: $successcolor;
  }

  .toast-type-warning {
    background-color: $warningcolor;
  }

  .toast-type-danger {
    background-color: $dangercolor;
  }
}

#old-browser-banner {
  background-color: #008;
  color: white;
  text-align: center;
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.25);
}

#read-only-banner {
  background-color: #d00;
  color: white;
  text-align: center;
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.25);
}

#sandbox-banner {
  background-color: #008;
  color: white;
  text-align: center;
  box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.25);
  padding: 0.5em;
  p {
    margin: 0;
  }
  a {
    color: white;
  }
  button.btn-link {
    color: white;
  }
}

@media print {
  #main-sidebar {
    display: none !important;
  }
}

.expanded-menu {
  width: 200px;
  transition: width 0.25s ease-out;
}

.shrunk-menu {
  width: 50px;
  transition: width 0.25s ease-out;
}

.expanded-body {
  margin-left: 50px;
  transition: margin 0.25s ease-out;
}

.shrunk-body {
  margin-left: 200px;
  transition: margin 0.25s ease-out;
}
</style>
